import { memo } from 'react';

import { type ClipMetadata } from '@air/api';
import { MONTH_DAY_YEAR_FORMAT } from '@air/utilities/src/Time';

import { KeyValueInfoRow } from '~/components/AssetModalV2/InfoPanel/components/KeyValueInfoRow';
import { SectionDisplay } from '~/components/AssetModalV2/InfoPanel/components/SectionDisplay';
import { formatMetadataDate, isNumberMetadataType, isStringMetadataType } from '~/utils/iptcDataTypes';

export type CameraDetailsSectionProps = {
  clipMetadata: ClipMetadata[];
};

export const CameraDetailsSection = memo(({ clipMetadata }: CameraDetailsSectionProps) => {
  const cameraMake = clipMetadata.find((metadata) => metadata.fieldName === 'Make');
  const cameraModel = clipMetadata.find((metadata) => metadata.fieldName === 'Model');
  const iso = clipMetadata.find((metadata) => metadata.fieldName === 'ISO');
  const aperture = clipMetadata.find((metadata) => metadata.fieldName === 'FNumber');
  const exposureTime = clipMetadata.find((metadata) => metadata.fieldName === 'ExposureTime');
  const focalLength = clipMetadata.find((metadata) => metadata.fieldName === 'FocalLength');
  const dateTaken = clipMetadata.find((metadata) => metadata.fieldName === 'DateTimeOriginal');

  if (
    !cameraMake?.data &&
    !cameraModel?.data &&
    !iso?.data &&
    !aperture?.data &&
    !exposureTime?.data &&
    !focalLength?.data &&
    !dateTaken?.data
  ) {
    return null;
  }

  const formattedDateTaken = dateTaken?.data ? formatMetadataDate(dateTaken, MONTH_DAY_YEAR_FORMAT) : null;

  return (
    <SectionDisplay title="Camera Info" data-testid="CAMERA_DETAILS_SECTION">
      <div className="flex flex-col gap-2">
        {!!formattedDateTaken && (
          <KeyValueInfoRow
            label="Date Taken"
            value={formattedDateTaken}
            data-testid="CAMERA_DETAILS_SECTION_DATE_TAKEN_ROW"
          />
        )}
        {!!exposureTime?.data && isStringMetadataType(exposureTime) && (
          <KeyValueInfoRow
            label="Exposure"
            value={exposureTime.data}
            data-testid="CAMERA_DETAILS_SECTION_EXPOSURE_ROW"
          />
        )}
        {!!aperture?.data && isNumberMetadataType(aperture) && (
          <KeyValueInfoRow
            label="Aperture"
            value={`ƒ/${aperture.data}`}
            data-testid="CAMERA_DETAILS_SECTION_APERTURE_ROW"
          />
        )}
        {!!focalLength?.data && isStringMetadataType(focalLength) && (
          <KeyValueInfoRow
            label="Focal Length"
            value={focalLength.data}
            data-testid="CAMERA_DETAILS_SECTION_FOCAL_LENGTH_ROW"
          />
        )}
        {!!iso?.data && isNumberMetadataType(iso) && (
          <KeyValueInfoRow label="ISO" value={iso.data.toString()} data-testid="CAMERA_DETAILS_SECTION_ISO_ROW" />
        )}
        {!!cameraMake?.data && isStringMetadataType(cameraMake) && (
          <KeyValueInfoRow label="Make" value={cameraMake.data} data-testid="CAMERA_DETAILS_SECTION_MAKE_ROW" />
        )}
        {!!cameraModel?.data && isStringMetadataType(cameraModel) && (
          <KeyValueInfoRow label="Model" value={cameraModel.data} data-testid="CAMERA_DETAILS_SECTION_MODEL_ROW" />
        )}
      </div>
    </SectionDisplay>
  );
});

CameraDetailsSection.displayName = 'CameraDetailsSection';
